import React, { useEffect, useState } from 'react';
import { Icon, Menu, Popup, Rating, Table } from 'semantic-ui-react'
import { load, setTotalRows, setCurrentPage } from './store'
import { useSelector, useDispatch } from 'react-redux'
import { loadData,
  ROWS_PER_PAGE,
  no_filters
} from './utilities.js';

function DataTable() {

  const dispatch = useDispatch()
  const { pageData, totalRows, currentPage, clinicalSignificance, gene } = useSelector((state) => state.clinvarData)

  const [isLoading, setIsLoading] = useState(false)

  const handleRemoteFetch = async (pageToLoad) => {

    if(isLoading) {
      return
    }

    setIsLoading(true)

    const startIndex = pageToLoad * ROWS_PER_PAGE
    const endIndex = startIndex + ROWS_PER_PAGE

    try {
      const data = await loadData({ 'gene': gene, 'clinical_significance': clinicalSignificance }, startIndex, endIndex)
      dispatch(load(data['rows']))
      dispatch(setTotalRows(data['totalRows']))
      dispatch(setCurrentPage(pageToLoad))

      setIsLoading(false)
    } catch (error) {
      console.error(error.message)
    }
  }

  useEffect(() => {
    handleRemoteFetch(0)
  }, []);

  const getTotalPages = () => {
    return ROWS_PER_PAGE > 0 ?
      Math.ceil(totalRows / ROWS_PER_PAGE )
      : 0
  }

  const canPageLeft = () => {
    return currentPage > 0
  }

  const pageLeft = () => {
    // If filters have been cleared, fetch the previous page of remote data,
    // otherwise get the PREVIOUS page of the pre-fetched data:
    no_filters({ 'gene': gene, 'clinical_significance': clinicalSignificance }) ?
      handleRemoteFetch(Math.max(0, currentPage - 1)) :
      dispatch(setCurrentPage(Math.max(0, currentPage - 1)))
  }

  const canPageRight = () => {
    return currentPage < getTotalPages() - 1
  }

  const pageRight = () => {
    // If filters have been cleared, fetch the next page of remote data,
    // otherwise get the NEXT page of the pre-fetched data:
    no_filters({ 'gene': gene, 'clinical_significance': clinicalSignificance }) ?
      handleRemoteFetch(Math.min(getTotalPages(), currentPage + 1)) :
      dispatch(setCurrentPage(Math.min(getTotalPages(), currentPage + 1)))
  }

  return (
    <Table basic='very' style={{ 'align': 'center' }}>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell colSpan='8'>
            <Menu floated='right' pagination secondary>
              <Menu.Item disabled style={{ 'color': 'black' }}>
                {
                  isLoading && 'Loading...'
                }
              </Menu.Item>
              <Menu.Item as='a' icon onClick={pageLeft} disabled={!canPageLeft()}>
                <Icon name='left chevron' />
              </Menu.Item>
              <Menu.Item disabled style={{ 'color': 'black' }}>
                Page {currentPage + 1} of {getTotalPages()}
              </Menu.Item>
              <Menu.Item as='a' icon onClick={pageRight} disabled={!canPageRight()}>
                <Icon name='right chevron' />
              </Menu.Item>
            </Menu>
          </Table.HeaderCell>
        </Table.Row>
        <Table.Row>
          <Table.HeaderCell collapsing>
            Variant &nbsp;
            <Popup
              trigger={<Icon name='help circle outline' />}
              content='Genetic variant reported in NIH ClinVar database.'
              size='small'
            />
          </Table.HeaderCell>
          <Table.HeaderCell collapsing>
            Gene &nbsp;
            <Popup
              trigger={<Icon name='help circle outline' />}
              content='Gene that contains this variant.'
              size='small'
            />
          </Table.HeaderCell>
          <Table.HeaderCell collapsing>
            <a href='https://www.ensembl.org/info/genome/variation/prediction/predicted_data.html#consequence_type_table'>Consequence </a>
            <Popup
              trigger={<Icon name='help circle outline' />}
              content='Variant Effect Predictor (VEP) annotation describing how the variant affects the gene.'
              size='small'
            />
          </Table.HeaderCell>
          <Table.HeaderCell>
            <a href="https://www.ncbi.nlm.nih.gov/clinvar/docs/variation_report/#interpretation">Clinical Significance </a>
            <Popup
              trigger={<Icon name='help circle outline' />}
              content='Clinical significance of the variant as reported in the NIH ClinVar database.'
              size='small'
            />
          </Table.HeaderCell>
          <Table.HeaderCell collapsing>
            <a href="https://www.ncbi.nlm.nih.gov/clinvar/docs/variation_report/#review_status">Stars </a>
            <Popup
              trigger={<Icon name='help circle outline' />}
              content='A measure of confidence in the Clinical Significance assertion.'
              size='small'
            />
          </Table.HeaderCell>
          <Table.HeaderCell collapsing>
            <a href="http://gnomad.broadinstitute.org/about">Population Frequency </a>
            <Popup
              trigger={<Icon name='help circle outline' />}
              content='How frequently this variant was observed in the gnomAD exomes dataset.'
              size='small'
            />
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {
          pageData.map((row, i) => {
            const pos = parseInt(row.pos, 10)
            const ucscLink=`http://genome.ucsc.edu/cgi-bin/hgTracks?db=hg19&highlight=hg19.chr${row.chrom}:${pos}-${pos}&position=chr${row.chrom}:${pos-30}-${pos+30}`
            const gtexLink = `https://www.gtexportal.org/home/gene/${row.symbol}`
            const clinvarLink = `https://www.ncbi.nlm.nih.gov/clinvar/variation/${row.measureset_id}/`
            const gnomadLink = `http://gnomad.broadinstitute.org/variant/${`${row.chrom}-${row.pos}-${row.ref}-${row.alt}`}`
            return  (
              <Table.Row key={i}>
                <Table.Cell style={{ 'minWidth': '15em' }}>
                  <a target='_blank' href={ucscLink}>
                    {`chr${row.chrom}:${row.pos} ${row.ref} > ${row.alt}`}
                  </a>
                </Table.Cell>
                <Table.Cell style={{ 'minWidth': '8em' }}>
                  <a target='_blank' href={gtexLink}>{row.symbol}</a>
                </Table.Cell>
                <Table.Cell style={{ 'minWidth': '15em' }}>
                  {
                    row.molecular_consequence ?
                      row.molecular_consequence.replace('variant', '').split(":").slice(-1).pop()
                      : null
                  }
                </Table.Cell>
                <Table.Cell>
                  <a target='_blank' href={clinvarLink}>
                    {row.clinical_significance}
                  </a>
                </Table.Cell>
                <Table.Cell style={{ 'minWidth': '150px' }}>
                  <Popup
                    trigger={
                      <Rating key={`${row.chrom}:${row.pos}`} icon='star' defaultRating={row.gold_stars} maxRating={5} disabled />
                    }
                    content={row.review_status}
                    size='small'
                    position='right center'
                  />
                </Table.Cell>
                <Table.Cell>
                  {
                    row.AC ?
                      <a target='_blank' href={gnomadLink}>{row.AC} out of {row.AN}</a>
                      : 'not observed'
                  }
                </Table.Cell>
              </Table.Row>)
          })
        }
      </Table.Body>
    </Table>
  )
}

export default DataTable
